import React, { useCallback, useEffect, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { DCard } from './MarketplaceAtoms.js';
import {
  SOCIAL_NETWORKS,
  SOCIAL_CONTENT_TYPES,
  formatKitAsDownloadText,
  downloadTextFile,
  downloadImageFromUrl,
  copyToClipboard,
  truncateForNetwork,
  buildPublicContentUrl,
} from '../../lib/socialPublishKit.js';
import { fetchSocialKit, publishSocialToN8n, processSocialQueue, enqueueSocialPublish } from '../../lib/socialPublishService.js';

const btnMini = {
  padding: '6px 12px',
  borderRadius: 8,
  border: '0.5px solid rgba(200,168,75,0.35)',
  background: 'rgba(200,168,75,0.08)',
  color: C.gold,
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
};

/**
 * Modal admin: kit manual + envío a n8n por red.
 * @param {{ open: boolean, onClose: () => void, contentType: string, contentId: string, titleHint?: string, onError?: (msg: string) => void, onSuccess?: (msg: string) => void }} props
 */
export default function AdminSocialKitModal({
  open,
  onClose,
  contentType,
  contentId,
  titleHint = '',
  onError,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [kit, setKit] = useState(null);
  const [activeNetwork, setActiveNetwork] = useState('telegram');
  const [publishing, setPublishing] = useState(null);
  const [flash, setFlash] = useState('');
  const [copied, setCopied] = useState(false);
  const [publications, setPublications] = useState([]);
  const [queueBusy, setQueueBusy] = useState(false);
  const [enqueueBusy, setEnqueueBusy] = useState(false);

  const loadKit = useCallback(async () => {
    if (!open || !contentId) return;
    setLoading(true);
    setKit(null);
    onError?.('');
    try {
      const res = await fetchSocialKit({ content_type: contentType, content_id: contentId });
      if (!res.ok) {
        onError?.(res.error || 'No se pudo cargar el kit.');
        return;
      }
      const publicUrl = buildPublicContentUrl(contentType, contentId);
      const kit = res.kit;
      if (kit && publicUrl && kit.url !== publicUrl) {
        const oldUrl = kit.url;
        const texts =
          kit.texts && typeof kit.texts === 'object'
            ? Object.fromEntries(
                Object.entries(kit.texts).map(([k, v]) => [
                  k,
                  oldUrl && typeof v === 'string' ? v.split(oldUrl).join(publicUrl) : v,
                ]),
              )
            : kit.texts;
        setKit({ ...kit, url: publicUrl, texts });
      } else {
        setKit(kit);
      }
    } catch (e) {
      onError?.(e?.message || 'Error al cargar el kit.');
    } finally {
      setLoading(false);
    }
  }, [open, contentId, contentType, onError]);

  useEffect(() => {
    if (open) loadKit();
    else {
      setKit(null);
      setFlash('');
      setCopied(false);
      setActiveNetwork('telegram');
    }
  }, [open, loadKit]);

  const showFlash = msg => {
    setFlash(msg);
    setTimeout(() => setFlash(''), 5000);
  };

  const displayTitle = kit?.titulo || titleHint || '—';
  const typeLabel = SOCIAL_CONTENT_TYPES[contentType]?.label || contentType;
  const netMeta = SOCIAL_NETWORKS.find(n => n.id === activeNetwork);
  const previewText =
    kit?.texts?.[activeNetwork] ??
    (kit ? truncateForNetwork(formatKitAsDownloadText(kit), netMeta?.maxChars ?? 2000) : '');

  const handleCopy = async () => {
    const ok = await copyToClipboard(previewText);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showFlash('Texto copiado.');
    } else {
      onError?.('No se pudo copiar. Selecciona el texto manualmente.');
    }
  };

  const handleDownloadKit = () => {
    if (!kit) return;
    const slug = String(displayTitle)
      .slice(0, 40)
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-');
    downloadTextFile(`dreamvinyl-${contentType}-${slug || contentId}.txt`, formatKitAsDownloadText(kit));
    showFlash('Kit descargado.');
  };

  const handleDownloadImage = async () => {
    const url = kit?.imagen_urls?.[0];
    if (!url) {
      showFlash('Sin imagen en este contenido.');
      return;
    }
    const ok = await downloadImageFromUrl(url, `dreamvinyl-${contentType}.jpg`);
    showFlash(ok ? 'Imagen descargada.' : 'Imagen abierta en nueva pestaña (descarga manual).');
  };

  const handlePublishN8n = async () => {
    if (!kit) return;
    setPublishing(activeNetwork);
    onError?.('');
    try {
      const res = await publishSocialToN8n({
        content_type: contentType,
        content_id: contentId,
        network: activeNetwork,
      });
      if (!res.ok) {
        onError?.(res.error || 'n8n no publicó el contenido.');
        return;
      }
      const extra = res.post_url ? ` URL: ${res.post_url}` : '';
      showFlash(`Enviado a n8n (${activeNetwork}).${extra}`);
      onSuccess?.(`Publicación enviada a ${activeNetwork} vía n8n.`);
      if (res.post_url) {
        setPublications(prev => {
          const rest = prev.filter(p => p.network !== activeNetwork);
          return [...rest, { network: activeNetwork, post_url: res.post_url, published_at: new Date().toISOString() }];
        });
      }
    } catch (e) {
      onError?.(e?.message || 'Error al enviar a n8n.');
    } finally {
      setPublishing(null);
    }
  };

  const handleEnqueue = async () => {
    if (!contentId) return;
    setEnqueueBusy(true);
    onError?.('');
    try {
      const res = await enqueueSocialPublish(contentType, contentId);
      if (!res.ok) {
        onError?.(res.error || 'No se pudo encolar.');
        return;
      }
      showFlash('Añadido a la cola de redes.');
    } catch (e) {
      onError?.(e?.message || 'Error al encolar.');
    } finally {
      setEnqueueBusy(false);
    }
  };

  const handleProcessQueue = async () => {
    setQueueBusy(true);
    onError?.('');
    try {
      const res = await processSocialQueue(5);
      if (!res.ok) {
        onError?.(res.error || 'No se procesó la cola.');
        return;
      }
      showFlash(`Cola: ${res.processed ?? 0} elemento(s) procesados.`);
      await loadKit();
    } catch (e) {
      onError?.(e?.message || 'Error al procesar cola.');
    } finally {
      setQueueBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Kit para redes sociales"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2015,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
      onClick={() => !publishing && !loading && onClose()}
    >
      <div
        role="presentation"
        onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 640, maxHeight: '92vh', overflow: 'auto' }}
      >
        <DCard style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>Kit para redes</div>
              <div style={{ marginTop: 6, fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                {typeLabel} · Manual o automatización vía n8n
              </div>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#f2f2f2',
                  lineHeight: 1.35,
                }}
              >
                {displayTitle}
              </div>
            </div>
            <button
              type="button"
              aria-label="Cerrar"
              disabled={!!publishing || loading}
              onClick={onClose}
              style={{
                flexShrink: 0,
                width: 32,
                height: 32,
                borderRadius: 8,
                border: '0.5px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.06)',
                color: '#fff',
                cursor: 'pointer',
                fontSize: 18,
              }}
            >
              ×
            </button>
          </div>

          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
              Generando textos e imágenes…
            </div>
          ) : !kit ? (
            <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
              No hay kit disponible. Comprueba que el contenido esté publicado / activo / aprobado.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                {SOCIAL_NETWORKS.map(n => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => setActiveNetwork(n.id)}
                    style={{
                      ...btnMini,
                      borderColor: activeNetwork === n.id ? 'rgba(200,168,75,0.65)' : btnMini.border,
                      background: activeNetwork === n.id ? 'rgba(200,168,75,0.2)' : btnMini.background,
                    }}
                  >
                    {n.label}
                  </button>
                ))}
              </div>

              {activeNetwork === 'telegram' ? (
                <div
                  style={{
                    marginBottom: 12,
                    padding: 10,
                    borderRadius: 8,
                    background: 'rgba(120,180,255,0.12)',
                    border: '1px solid rgba(120,180,255,0.35)',
                    fontSize: 11,
                    color: 'rgba(200,220,255,0.95)',
                    lineHeight: 1.45,
                  }}
                >
                  Envía foto + texto al grupo <strong>@DreamVinylComunidad</strong> (semi-manual para otras redes). Un solo
                  clic = una red; elige Instagram/Facebook aparte si quieres publicar allí también.
                </div>
              ) : null}

              {activeNetwork === 'x' ? (
                <div
                  style={{
                    marginBottom: 12,
                    padding: 10,
                    borderRadius: 8,
                    background: 'rgba(226,160,80,0.12)',
                    border: '1px solid rgba(226,160,80,0.35)',
                    fontSize: 11,
                    color: 'rgba(255,220,180,0.95)',
                    lineHeight: 1.45,
                  }}
                >
                  La API de X es de pago. Usa <strong>Copiar texto</strong> y publica a mano, o configura n8n solo si
                  tienes plan de desarrollador.
                </div>
              ) : null}

              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 6 }}>
                {previewText.length} / {netMeta?.maxChars ?? '—'} caracteres · {netMeta?.automationNote}
              </div>

              <textarea
                readOnly
                value={previewText}
                rows={8}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: 12,
                  borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.12)',
                  background: 'rgba(0,0,0,0.35)',
                  color: '#eee',
                  fontSize: 12,
                  lineHeight: 1.5,
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />

              {kit.url ? (
                <div style={{ marginTop: 8, fontSize: 11, color: 'rgba(255,255,255,0.55)', wordBreak: 'break-all' }}>
                  Enlace:{' '}
                  <a href={kit.url} target="_blank" rel="noopener noreferrer" style={{ color: C.gold }}>
                    {kit.url}
                  </a>
                </div>
              ) : null}

              {publications.length ? (
                <div style={{ marginTop: 12, fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                  <div style={{ fontWeight: 700, color: C.gold, marginBottom: 6 }}>Publicado en web (URLs guardadas)</div>
                  <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                    {publications.map(p => (
                      <li key={p.network}>
                        <strong>{p.network}</strong>:{' '}
                        <a href={p.post_url} target="_blank" rel="noopener noreferrer" style={{ color: C.gold }}>
                          {p.post_url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {kit.imagen_urls?.[0] ? (
                <div style={{ marginTop: 12 }}>
                  <img
                    src={kit.imagen_urls[0]}
                    alt=""
                    style={{
                      maxWidth: '100%',
                      maxHeight: 160,
                      borderRadius: 8,
                      objectFit: 'contain',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  />
                </div>
              ) : null}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
                <button type="button" style={btnMini} onClick={handleCopy}>
                  {copied ? 'Copiado ✓' : 'Copiar texto'}
                </button>
                <button type="button" style={btnMini} onClick={handleDownloadImage} disabled={!kit.imagen_urls?.length}>
                  Descargar imagen
                </button>
                <button type="button" style={btnMini} onClick={handleDownloadKit}>
                  Descargar kit (.txt)
                </button>
                <button
                  type="button"
                  style={{ ...btnMini, borderColor: 'rgba(120,180,255,0.5)', color: 'rgba(180,210,255,0.95)' }}
                  disabled={!!publishing}
                  onClick={handlePublishN8n}
                >
                  {publishing === activeNetwork ? 'Enviando…' : 'Enviar a n8n'}
                </button>
                <button
                  type="button"
                  style={btnMini}
                  disabled={!!publishing || enqueueBusy}
                  onClick={handleEnqueue}
                >
                  {enqueueBusy ? 'Encolando…' : 'Encolar en redes'}
                </button>
                <button
                  type="button"
                  style={{ ...btnMini, borderColor: 'rgba(29,158,117,0.45)', color: '#9fe8ca' }}
                  disabled={!!publishing || queueBusy}
                  onClick={handleProcessQueue}
                >
                  {queueBusy ? 'Cola…' : 'Procesar cola'}
                </button>
              </div>

              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  borderRadius: 8,
                  background: 'rgba(255,255,255,0.04)',
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.45)',
                  lineHeight: 1.5,
                }}
              >
                <strong style={{ color: 'rgba(255,255,255,0.65)' }}>n8n:</strong> Telegram usa{' '}
                <code>N8N_SOCIAL_PUBLISH_WEBHOOK_URL</code>; el resto de redes usa{' '}
                <code>N8N_SOCIAL_MULTI_WEBHOOK_URL</code> (workflow y path distintos). La pestaña activa envía{' '}
                <code>network</code> en el body. Requiere n8n + ngrok activos.
              </div>
            </>
          )}

          {flash ? (
            <div style={{ marginTop: 12, fontSize: 12, color: '#5DCAA5', fontWeight: 600 }}>{flash}</div>
          ) : null}
        </DCard>
      </div>
    </div>
  );
}
